import * as C from '../../constants';
import SkiPassService from '../../../../services/ski/ski-pass-service';
import BookingSkiPassService from '../../../../services/ski/booking-ski-pass-service';
import exceptionValidator from '../../../../utils/exceptionValidator';
import BookingService from '../../../../services/booking-service';

export default {

  /**
   * @param commit
   */
  resetState({ commit }) {
    commit(C.SKI_PASS_RESET_STATE);
  },

  /**
   * @param commit
   * @param payload
   * @returns {Promise<T>}
   */
  availablePassPricesLoad({ commit }, payload) {
    commit(C.SKI_PASS_AVAILABLE_PRODUCTS_LOAD_PENDING);

    return SkiPassService.searchAvailableSkiPassPrices(payload)
      .then((res) => {
        commit(C.SKI_PASS_AVAILABLE_PRODUCTS_LOAD_SUCCESS, res.data);
      })
      .catch((error) => {
        commit(C.SKI_PASS_AVAILABLE_PRODUCTS_LOAD_FAILURE, error.response.data);
      });
  },

  /**
   * @param commit
   * @param bookingId
   * @returns {Promise<void>|Promise<T>}
   */
  passByBookingIdLoad({ commit }, bookingId) {
    commit(C.SKI_PASS_PRODUCTS_LOAD_PENDING);

    return BookingSkiPassService.getSkiPassByBookingId(bookingId)
      .then((res) => {
        commit(C.SKI_PASS_PRODUCTS_LOAD_SUCCESS, res.data ? res.data : []);
      })
      .catch((error) => {
        commit(C.SKI_PASS_PRODUCTS_LOAD_FAILURE, error.response.data);
      });
  },

  /**
   * @param commit
   * @param dispatch
   * @param payload
   * @returns {Promise<T>}
   */
  passCreate({ commit, dispatch }, payload) {
    commit(C.SKI_PASS_CREATE_PENDING);

    return BookingSkiPassService.createSkiPass(payload.bookingParticipantId, payload.skiPass)
      .then(() => {
        commit(C.SKI_PASS_CREATE_SUCCESS);
        dispatch('passByBookingIdLoad', payload.bookingId);
      })
      .catch((error) => {
        const errorMessages = exceptionValidator.parseErrorException(error, true);
        commit(C.SKI_PASS_CREATE_FAILURE, errorMessages);
      });
  },

  /**
   * @param commit
   * @param state
   * @param payload
   * @returns {Promise<T>}
   */
  passSupplierRefUpdate({ commit, state }, payload) {
    commit(C.SKI_PASS_SUPPLIER_REF_UPDATE_PENDING);

    return BookingSkiPassService.updateSkiPassWithSupplierRef(
      payload.skiPass.id,
      payload.supplierRef,
    )
      .then(() => {
        const currentSkiPass = {
          ...Object.assign({}, payload.skiPass),
          supplierRef: payload.supplierRef,
        };
        const items = Object.values(state.pass.items).map(
          pass => (
            pass.id === currentSkiPass.id ? currentSkiPass : pass
          ),
        );
        commit(C.SKI_PASS_SUPPLIER_REF_UPDATE_SUCCESS, items);
      })
      .catch((error) => {
        const errorMessages = exceptionValidator.parseErrorException(error, true);
        commit(C.SKI_PASS_SUPPLIER_REF_UPDATE_FAILURE, errorMessages);
      });
  },

  /**
   * @param commit
   * @param dispatch
   * @param payload
   * @returns {Promise<T>}
   */
  passCancel({ commit, dispatch }, payload) {
    commit(C.SKI_PASS_CANCEL_PENDING);

    return BookingSkiPassService.cancelSkiPass(payload.skiPassId, payload.salesAgentUserId)
      .then(() => {
        commit(C.SKI_PASS_CANCEL_SUCCESS, payload.skiPassId);
        dispatch('passByBookingIdLoad', payload.bookingId);
        window.location.reload();
      })
      .catch((error) => {
        commit(C.SKI_PASS_CANCEL_FAILURE, error.response.data);
      });
  },

  /**
   * @param commit
   * @param dispatch
   * @param payload
   * @returns {Promise<T>}
   */
  reOpen({ commit, dispatch }, payload) {
    commit(C.SKI_PASS_CHANGE_STATUS_PENDING);

    return BookingSkiPassService.reOpen(payload.skiPassId)
      .then(() => {
        commit(C.SKI_PASS_CHANGE_STATUS_SUCCESS, payload.skiPassId);
        dispatch('passByBookingIdLoad', payload.bookingId);
      })
      .catch((error) => {
        commit(C.SKI_PASS_CHANGE_STATUS_FAILURE, error.response.data);
      });
  },

  /**
   * @param commit
   * @param dispatch
   * @param payload
   * @returns {Promise<T>}
   */
  passStop({ commit, dispatch }, payload) {
    commit(C.SKI_PASS_STOP_PENDING);

    return BookingSkiPassService.stopSkiPass(payload.skiPassId, payload.salesAgentUserId)
      .then(() => {
        commit(C.SKI_PASS_STOP_SUCCESS);
        dispatch('passByBookingIdLoad', payload.bookingId);
        window.location.reload();
      })
      .catch((error) => {
        commit(C.SKI_PASS_STOP_FAILURE, error.response.data);
      });
  },

  /**
   * @param commit
   * @param dispatch
   * @param payload
   * @returns {Promise<boolean>}
   */
  passUpdate({ commit, dispatch }, payload) {
    commit(C.SKI_PASS_UPDATE_PENDING);

    return BookingSkiPassService.updateSkiPass(payload.changedProduct)
      .then(() => {
        commit(C.SKI_PASS_UPDATE_SUCCESS);
        dispatch('passByBookingIdLoad', payload.bookingId);
        return Promise.resolve(true);
      })
      .catch((error) => {
        const errorMessages = exceptionValidator.parseErrorException(error, true);
        commit(C.SKI_PASS_UPDATE_FAILURE, errorMessages);
      });
  },

  /**
   * @param commit
   * @param dispatch
   * @param payload
   * @returns {Promise<boolean>}
   */
  passPriceUpdate({ commit, dispatch }, payload) {
    commit(C.SKI_PASS_PRICE_UPDATE_PENDING);

    return BookingSkiPassService.updateSkiPassPrice(payload.changedProductPrice)
      .then(() => {
        commit(C.SKI_PASS_PRICE_UPDATE_SUCCESS);
        dispatch('passByBookingIdLoad', payload.bookingId);
        return Promise.resolve(true);
      })
      .catch((error) => {
        const errorMessages = exceptionValidator.parseErrorException(error, true);
        commit(C.SKI_PASS_PRICE_UPDATE_FAILURE, errorMessages);
      });
  },

  /**
   * @param commit
   * @param state
   * @param payload
   * @returns {Promise<T>}
   */
  passDelete({ commit, state }, payload) {
    commit(C.SKI_PASS_DELETE_PENDING);

    return BookingSkiPassService.deleteSkiPass(payload.skiPassId, payload.salesAgentUserId)
      .then(() => {
        const items = Object.values(state.pass.items).filter(pass => pass.id !== payload.skiPassId);
        commit(C.SKI_PASS_DELETE_SUCCESS, items);
        window.location.reload();
      })
      .catch((error) => {
        commit(C.SKI_PASS_DELETE_FAILURE, error.response.data);
      });
  },

  /**
   * @param commit
   * @param dispatch
   * @param payload
   * @returns {Promise<boolean>}
   */
  passCancellationFeeNetUpdate({ commit, dispatch }, payload) {
    commit(C.SKI_PASS_CANCELLATION_FEE_NET_UPDATE_PENDING);

    return BookingSkiPassService.updateSkiPassCancellationFeeNet(payload.skiPassId, payload.cancellationFeeNet)
      .then(() => {
        commit(C.SKI_PASS_CANCELLATION_FEE_NET_UPDATE_SUCCESS);
        dispatch('passByBookingIdLoad', payload.bookingId);
        return Promise.resolve(true);
      })
      .catch((error) => {
        const errorMessages = exceptionValidator.parseErrorException(error, true);
        commit(C.SKI_PASS_CANCELLATION_FEE_NET_UPDATE_FAILURE, errorMessages);
      });
  },

  /**
   * @param commit
   * @param bookingId
   * @returns {Promise<void>}
   */
  sendSkiPassRequestEmail({ commit }, bookingId) {
    commit(C.SKI_PASS_SEND_REQUEST_EMAIL_PENDING);
    return BookingSkiPassService.sendSkiPassRequestEmail(bookingId)
      .then(() => {
        commit(C.SKI_PASS_SEND_REQUEST_EMAIL_SUCCESS);
      })
      .catch((error) => {
        commit(C.SKI_PASS_SEND_REQUEST_EMAIL_FAILURE, error.response.data);
      });
  },

  /**
   * @param commit
   * @param payload
   */
  setBookingParticipant({ commit }, payload) {
    commit(C.SKI_PASS_SEARCH_BOOKING_PARTICIPANT_SET, payload);
  },

  /**
   * @param commit
   * @param payload
   */
  setStartDate({ commit }, payload) {
    commit(C.SKI_PASS_SEARCH_START_DATE_SET, payload);
  },

  /**
   * @param commit
   * @param payload
   */
  setNumberOfDays({ commit }, payload) {
    commit(C.SKI_PASS_SEARCH_NUMBER_OF_DAYS_SET, payload);
  },

  /**
   * @param commit
   * @param payload
   */
  setArea({ commit }, payload) {
    commit(C.SKI_PASS_SEARCH_AREA_SET, payload);
  },

  /**
   * @param commit
   * @param payload
   */
  setChangeProductBookingParticipant({ commit }, payload) {
    commit(C.SKI_PASS_CHANGE_PRODUCT_BOOKING_PARTICIPANT_SET, payload);
  },

  /**
   * @param commit
   * @param payload
   */
  setChangeProductStartDate({ commit }, payload) {
    commit(C.SKI_PASS_CHANGE_PRODUCT_START_DATE_SET, payload);
  },

  /**
   * @param commit
   * @param payload
   */
  setChangeProductNumberOfDays({ commit }, payload) {
    commit(C.SKI_PASS_CHANGE_PRODUCT_NUMBER_OF_DAYS_SET, payload);
  },

  /**
   * @param commit
   * @param payload
   */
  setChangeProductPersonCategory({ commit }, payload) {
    commit(C.SKI_PASS_CHANGE_PRODUCT_PERSON_CATEGORY_SET, payload);
  },

  getPickupPlaces({ commit, rootState }) {
    commit(C.BOOKING_GET_SKI_PASS_PICKUP_PLACES_PENDING);
    const bookingId = rootState.booking.booking.id;
    return BookingService.getPickupPlaces({ bookingId, skiRental: false }).then((res) => {
      commit(C.BOOKING_GET_SKI_PASS_PICKUP_PLACES_SUCCESS, res.data);
    }).catch(() => {
      commit(C.BOOKING_GET_SKI_PASS_PICKUP_PLACES_FAILURE);
    });
  },

  savePickupPlace({ commit, rootState }, id) {
    commit(C.BOOKING_SAVE_SKI_PASS_PICKUP_PLACE_PENDING);
    const bookingId = rootState.booking.booking.id;
    return BookingService.savePickupPlaces({ bookingId, id, skiRental: false }).then(() => {
      commit(C.BOOKING_SAVE_SKI_PASS_PICKUP_PLACE_SUCCESS);
    }).catch(() => {
      commit(C.BOOKING_SAVE_SKI_PASS_PICKUP_PLACE_FAILURE);
    });
  },

  assignParticipantToSkiPass({ commit, state }, payload) {
    commit(C.SKI_PASS_ASSIGN_PARTICIPANT_PENDING);
    return BookingSkiPassService.assignParticipantToSkiPass(payload).then(() => {
      const skiPasses = JSON.parse(JSON.stringify(state.pass.items));
      const skiPassIndex = skiPasses.findIndex(pass => pass.id === payload.skiPassId);
      skiPasses[skiPassIndex].bookingParticipantId = payload.participantId;
      commit(C.SKI_PASS_ASSIGN_PARTICIPANT_SUCCESS, skiPasses);
    }).catch((error) => {
      commit(C.SKI_PASS_ASSIGN_PARTICIPANT_FAILURE, error.response.data);
    });
  },

  bookByXML({ commit, state }, payload) {
    const skiPasses = state.pass.items;
    commit(C.SKI_PASS_PARTICIPANT_BOOK_BY_XML_PENDING);
    return BookingSkiPassService.bookByXML(payload).then(() => {
      commit(C.SKI_PASS_PARTICIPANT_BOOK_BY_XML_SUCCESS, skiPasses);
    }).catch((error) => {
      commit(C.SKI_PASS_PARTICIPANT_BOOK_BY_XML_FAILURE, error);
    });
  },

  updateSupplierRefPubnub({ commit, state }, payload) {
    const skiPasses = JSON.parse(JSON.stringify(state.pass.items));
    const skiPassIndex = skiPasses.findIndex(pass => pass.id === payload.id);
    skiPasses[skiPassIndex].supplierRef = payload.supplierRef;
    commit(C.SKI_PASS_PARTICIPANT_BOOK_BY_XML_SUCCESS, skiPasses);
  },
};



// WEBPACK FOOTER //
// ./src/store/modules/ski/pass/actions.js